/**
 * The English copy, and the *shape* of every other language's.
 *
 * `Strings` is derived from this object (see `./index.ts`), so adding a
 * line here is a type error in `nl.ts` until it is translated — there is
 * no lookup-by-key path and therefore no missing-key fallback to English
 * at runtime, which is the failure mode a string-map i18n usually ships
 * with. Interpolated copy is a function rather than a template with
 * placeholders, for the same reason: the argument list is type-checked.
 *
 * Grouped by where the copy appears, not by feature, so translating a
 * screen means reading one contiguous block.
 *
 * Deliberately not `as const`: `Strings` is `typeof en`, and const-asserted
 * literals would type every line as *the English text itself*, which no
 * translation could satisfy.
 */
export const en = {
  common: {
    close: 'Close',
    menu: 'Menu',
    /** The footer logo button. Distinct from `menu` so the two are tellable apart by name. */
    openMenu: 'Open menu',
    statistics: 'Statistics',
    backToToday: 'Back to today’s puzzle',
    toToday: '← Today’s puzzle',
    viewResult: 'View result',
    tryAgain: 'Try again',
    copied: 'Copied to clipboard',
    shareFailed: 'Could not share result',
    loading: 'Loading…',
  },

  header: {
    guessGrid: 'Guess grid',
    keyboard: 'Keyboard',
    backspace: 'Backspace',
    enter: 'ENTER',
    boardLocked: 'Board locked',
    keyLabel: (letter: string, state: string) => `${letter}, ${state}`,
    keyState: {
      correct: 'correct',
      present: 'present',
      absent: 'absent',
      unused: 'unused',
    },
  },

  menu: {
    title: 'Menu',
    todaysPuzzle: 'Today’s puzzle',
    journey: 'Journey',
    newChallenge: 'New challenge',
    myChallenges: 'My challenges',
    howToPlay: 'How to play',
    about: 'About',
    language: 'Language',
    languageHint: 'Puzzles, definitions and the app itself. Progress is kept per language.',
  },

  daily: {
    sectionLabel: 'Daily challenge',
    heading: 'DAILY CHALLENGE',
    letters: (n: number) => `${n} LETTERS`,
    loading: 'Loading today’s puzzle…',
    challengeAFriend: 'Challenge a friend',
  },

  stats: {
    yourStats: 'Your stats',
    dayStreak: 'DAY STREAK',
    wins: 'WINS',
    totalXp: 'TOTAL XP',
    streakLabel: (n: number) => `${n} day streak`,
    winsLabel: (n: number) => `${n} wins`,
    xpLabel: (n: number) => `${n} total XP`,
    played: 'PLAYED',
    winPercent: 'WIN %',
    streak: 'STREAK',
    max: 'MAX',
    totalXpRow: 'Total XP',
    distribution: 'GUESS DISTRIBUTION',
    distributionLabel: 'Guess distribution',
    noGames: 'Play a game to build your stats.',
    distributionRow: (guesses: number, count: number) => `${guesses} guesses: ${count}`,
  },

  board: {
    guessOf: (n: number, total: number) => `Guess ${n} of ${total}`,
    solvedIn: (n: number) => `Solved in ${n} guesses`,
    outOfGuessesWord: (word: string) => `Out of guesses. The word was ${word}`,
  },

  hint: {
    label: 'HINT',
    perHint: '−10 XP each',
    left: (n: number) => `Hint, ${n} left`,
    groupLabel: (revealed: number, total: number) => `Revealed hint letters, ${revealed} of ${total} revealed`,
    position: (index: number, letter: string) => `position ${index}: ${letter}`,
    allRevealed: 'Every letter is already revealed',
  },

  insight: {
    label: 'INSIGHT',
    left: (n: number) => `Insight, ${n} left`,
    title: 'Word insight',
    guessFirst: 'Guess a word first — insights explain words you have already played.',
    reveal: 'Reveal',
    revealFor: (word: string) => `Reveal insight for ${word}`,
    noEntry: 'No entry',
    unavailable: 'Definition unavailable offline.',
    origin: (text: string) => `Origin: ${text}`,
    leftToday: (n: number) => `${n} ${n === 1 ? 'insight' : 'insights'} left today`,
  },

  toast: {
    invalidWord: 'Not in word list',
    badLength: 'Not enough letters',
    gameOver: 'This game is finished',
    validationPending: 'Checking word list…',
  },

  result: {
    winHeadings: [
      'Genius', 'Brilliant', 'Impressive', 'Solid', 'Phew', 'Clutch', 'Nail-biter', 'By a thread',
    ],
    solved: 'Solved',
    lost: 'Out of guesses',
    dailySubtitle: (id: string | number) => `Lexicon #${id}`,
    dailySubtitleNoNumber: 'Lexicon',
    journeySubtitle: (letters: string | number) => `Journey — ${letters} letters`,
    challengeSubtitle: (code: string | number) => `Challenge ${code}`,
    answerLabel: 'Answer',
    noEntry: 'No dictionary entry for this word.',
    xpLabel: 'XP earned',
    base: 'Base',
    streakBonus: 'Streak bonus',
    journeyComplete: 'Journey complete',
    hints: 'Hints',
    dayStreak: 'Day streak',
    journeyNoStreak: 'Journey never changes your streak',
    challengeNoStreak: 'Challenges never change your streak',
    share: 'Share result',
  },

  journey: {
    label: 'JOURNEY',
    sectionLabel: 'Journey',
    loading: 'Loading your journey…',
    rungLabel: (rung: number, final: number) => `Journey rung ${rung} of ${final}`,
    complete: 'Journey complete!',
    completedCount: (n: number) => `×${n} completed`,
    best: (rung: number) => `BEST ${rung}`,
    startNew: 'Start a new journey',
    nextWord: (letters: number) => `Next word — ${letters} letters`,
    nextFailed: 'Could not start the next word — try again.',
  },

  challenge: {
    label: 'CHALLENGE',
    sectionLabel: 'Challenge',
    from: (name: string) => `from ${name}`,
    code: (code: string) => `Code ${code}`,
    offline: 'OFFLINE',
    loading: 'Loading challenge…',
    incompleteLink: 'This challenge link is incomplete.',
    expiredLink: 'This challenge link has expired or does not exist.',
    loadFailed: 'Could not load this challenge.',
    createYourOwn: 'Create your own challenge',
    sending: 'Sending your result…',
    sentAs: (name: string) => `Result sent as ${name}`,
    sentAnonymously: 'Result sent anonymously',
    sendFailed: 'Result could not be sent',
    offlineNotSent: 'Offline — result not sent',
    invite: (url: string) => `Can you solve my Lexicon challenge? ${url}`,
  },

  newChallenge: {
    title: 'New challenge',
    wordLabel: 'CHALLENGE WORD',
    wordPlaceholder: (min: number, max: number) => `${min}–${max} letter word`,
    nameLabel: 'YOUR NAME (OPTIONAL)',
    namePlaceholder: 'Anonymous',
    create: 'Create challenge',
    creating: 'Creating…',
    lengthError: (min: number, max: number) => `Enter a word of ${min} to ${max} letters`,
    listError: 'Could not load the word list. Try again.',
    notInList: 'Not in word list',
    rateLimited: 'You have hit today’s challenge limit. Try again tomorrow.',
    serverError: 'Could not reach the server.',
    ready: 'Challenge ready',
    readyLabel: 'Challenge ready',
    offlineTitle: 'Offline link',
    offlineLabel: 'Offline link',
    offlineBody: 'Your friend can still play from this link, but their result will not come back.',
    shareLink: 'Share link',
    copyLink: 'Copy link',
    /** The language a challenge word is checked against, when it is not the app's. */
    wordLanguageNote: (language: string) => `Checked against the ${language} word list.`,
  },

  myChallenges: {
    title: 'My challenges',
    none: 'No challenges yet.',
    create: 'Create a challenge',
    label: (code: string) => `Challenge ${code}`,
    meta: (code: string, date: string) => `Code ${code} · ${date}`,
    loadingResults: 'Loading results…',
    resultsFailed: 'Could not load results',
    retry: 'Retry',
    noPlayers: 'No one has played yet',
    anonymous: 'Anonymous',
    solvedIn: (name: string, guesses: number, rows: number) => `${name} — solved in ${guesses}/${rows}`,
    didNotSolve: (name: string) => `${name} — didn’t solve`,
    open: 'Open',
    shareLink: 'Share link',
  },

  howTo: {
    title: 'How to play',
    intro: 'Guess the day’s word in one more try than it has letters.',
    realWord: 'Every guess must be a real word. Tiles change colour to show how close you were.',
    example: {
      correct: 'Green — right letter, right spot.',
      present: 'Gold — right letter, wrong spot.',
      absent: 'Dark — not in the word.',
    },
    /** The example tiles spell a word; each language spells its own. */
    exampleLetters: ['W', 'O', 'D'],
    ladder: 'A new word unlocks every day at midnight, and its length follows the week: four letters '
      + 'on Monday, five Tuesday to Thursday, six on Friday and Saturday, seven on Sunday. '
      + 'Winning on consecutive days builds your streak.',
    journey: 'Journey is the endless one: climb four, five, six and seven letters in a row to finish a '
      + 'journey. A loss sends you back to four. Journey games pay half XP and never touch your streak.',
    hints: 'Hints reveal a letter for −10 XP. Insights explain a word you have already guessed. You '
      + 'get 2 hints and 3 insights a day.',
    languages: 'Play in English or Dutch — the menu switches the puzzle, the definitions and the app. '
      + 'Each language keeps its own streak, stats and XP.',
  },

  about: {
    title: 'About',
    tagline: 'Lexicon — sharpen your mind. Expand your lexicon.',
    body: 'A word game with a length that changes through the week, plus streaks, XP, hints, word '
      + 'insights, an endless Journey mode and challenges you can send to a friend. Playable in '
      + 'English and Dutch.',
    version: 'Version 1.1',
    privacy: 'Your streak, stats, and progress live only on this device. No account. The app sends one '
      + 'anonymous play ping a day so we can count players — no personal data, ever.',
    creditsEnglish: 'English definitions and etymologies come from Wiktionary via the kaikki.org extract, used under',
    creditsDutch: 'Dutch definitions come from Open Dutch WordNet, also under CC BY-SA 4.0. Dutch word lists come from the OpenTaal spelling list.',
    wordLists: 'English word lists derive from the original Wordle community lists.',
  },

  notFound: {
    title: 'Nothing here',
    body: 'That link doesn’t lead anywhere in Lexicon.',
  },
};
